import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    Button,
    CircularProgress,
    Grid,
    Paper,
    Typography,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from "@material-ui/core";
import firebase from "firebase/app";
import "firebase/database";
import * as XLSX from "xlsx";

export default function ExportInscritosByBusiness() {
    const events = useSelector((state) => state.events.events);

    const [businessId, setBusinessId] = useState("aguasabiertasyucatan");
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);

    const eventsArray = useMemo(() => {
        if (!events) return [];
        return Array.isArray(events) ? events : Object.values(events);
    }, [events]);

    const eventosFiltrados = useMemo(() => {
        if (!businessId) return [];

        return eventsArray.filter(
            (event) =>
                event &&
                String(event.businessId || "").trim().toLowerCase() ===
                String(businessId).trim().toLowerCase()
        );
    }, [eventsArray, businessId]);

    const calcularTotalOrden = (order) => {
        if (!Array.isArray(order)) return 0;

        return order.reduce((total, item) => {
            const precio = Number(item.unit_price || 0);
            const cantidad = Number(item.quantity || 0);
            return total + precio * cantidad;
        }, 0);
    };

    const getModalidadData = (modalidadesInscripcion, modalidadCode) => {
        if (!modalidadesInscripcion) return null;

        const modalidadesArray = Object.values(modalidadesInscripcion);

        return modalidadesArray.find(
            (mod) =>
                String(mod.codigoModalidad) === String(modalidadCode) ||
                String(mod.modalidad) === String(modalidadCode)
        );
    };

    const getCategoriaData = (modalidadData, rama, categoriaCode) => {
        if (!modalidadData || !modalidadData.ramaCat) return null;

        const ramaData = modalidadData.ramaCat[`rama_${rama}`];

        if (!ramaData || !ramaData.categorias) return null;

        const categoriasArray = Object.values(ramaData.categorias);

        return categoriasArray.find(
            (cat) => String(cat.codigo) === String(categoriaCode)
        );
    };

    const cargarInscritos = async () => {
        setLoading(true);
        setRows([]);

        try {
            const allRows = [];

            for (const evento of eventosFiltrados) {
                const eventoIndex = `evento_${evento.iDEvento}`;

                const [inscritosSnapshot, modalidadesSnapshot] = await Promise.all([
                    firebase
                        .database()
                        .ref(`eventosCompletos/${eventoIndex}/inscritos/listadoInscritos`)
                        .once("value"),

                    firebase
                        .database()
                        .ref(`eventosCompletos/${eventoIndex}/modalidadesInscripcion`)
                        .once("value"),
                ]);

                const inscritosObj = inscritosSnapshot.val() || {};
                const modalidadesInscripcion = modalidadesSnapshot.val() || {};

                Object.entries(inscritosObj).forEach(([inscritoKey, inscrito]) => {
                    const modalidadData = getModalidadData(
                        modalidadesInscripcion,
                        inscrito.modalidad
                    );

                    const categoriaData = getCategoriaData(
                        modalidadData,
                        inscrito.rama,
                        inscrito.categoria
                    );

                    allRows.push({
                        eventoIndex,
                        iDEvento: evento.iDEvento || "",
                        nombreEvento: evento.nombreEvento || "",
                        fechaEvento: evento.fechaEvento || "",
                        businessId: evento.businessId || "",

                        inscritoKey,
                        numero: inscrito.numero || "",
                        nombres: inscrito.nombres || "",
                        apellidos: inscrito.apellidos || "",
                        correo: inscrito.correo || "",
                        telefono: inscrito.telefono || "",
                        fechaNacimiento: inscrito.fechaNacimiento || "",

                        rama: inscrito.rama || "",

                        modalidadCodigo: inscrito.modalidad || "",
                        modalidadNombre: modalidadData?.descripcionModalidad || "",

                        categoriaCodigo: inscrito.categoria || "",
                        categoriaNombre: categoriaData?.nombreCategoria || "",
                        categoriaEdadDesde: categoriaData?.edadDesde || "",
                        categoriaEdadHasta: categoriaData?.edadHasta || "",
                        categoriaSerie: categoriaData?.serie || "",

                        fechaRegistro: inscrito.fechaRegistro || "",
                        serverTime: inscrito.serverTime || "",
                        tipoRegistro: inscrito.tipoRegistro || "",
                        usuarioRegistra: inscrito.usuarioRegistra || "",

                        statusPago: inscrito.statusPago?.status || "",
                        formaPago: inscrito.statusPago?.formaPago || "",
                        formaInscripcion: inscrito.statusPago?.formaInscripcion || "",
                        fechaPago: inscrito.statusPago?.fechaPago || "",
                        idOperacion: inscrito.statusPago?.idOperacion || "",

                        moneda: inscrito.monedaAUsar?.codigo || "",
                        totalOrder: calcularTotalOrden(inscrito.order),

                        orderJson: inscrito.order ? JSON.stringify(inscrito.order) : "",
                        cambiosJson: inscrito.changesRegistry
                            ? JSON.stringify(inscrito.changesRegistry)
                            : "",
                        mailsJson: inscrito.mails ? JSON.stringify(inscrito.mails) : "",

                        versionForm: inscrito.versionForm || "",
                    });
                });
            }

            setRows(allRows);

            if (!allRows.length) {
                alert("No se encontraron inscritos para los eventos seleccionados.");
            }
        } catch (error) {
            console.error("Error cargando inscritos:", error);
            alert("Ocurrió un error cargando los inscritos.");
        } finally {
            setLoading(false);
        }
    };
    const descargarExcel = async () => {
        await cargarInscritos();
    };

    const descargarArchivo = (data) => {
        if (!data.length) return;

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Inscritos");

        XLSX.writeFile(
            workbook,
            `inscritos_${businessId}_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
    };

    React.useEffect(() => {
        if (rows.length) {
            descargarArchivo(rows);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    return (
        <Paper style={{ padding: 20 }}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h6">
                        Exportar inscritos por Business ID
                    </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        label="Business ID"
                        variant="outlined"
                        fullWidth
                        value={businessId}
                        onChange={(e) => {
                            setBusinessId(e.target.value);
                            setRows([]);
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                        Eventos encontrados: {eventosFiltrados.length}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={loading || !eventosFiltrados.length}
                        onClick={descargarExcel}
                    >
                        {loading ? <CircularProgress size={22} /> : "Descargar inscritos"}
                    </Button>
                </Grid>

                <Grid item xs={12}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID Evento</TableCell>
                                <TableCell>Nombre evento</TableCell>
                                <TableCell>Fecha</TableCell>
                                <TableCell>Business ID</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {eventosFiltrados.map((event) => (
                                <TableRow key={event.iDEvento}>
                                    <TableCell>{event.iDEvento}</TableCell>
                                    <TableCell>{event.nombreEvento}</TableCell>
                                    <TableCell>{event.fechaEvento}</TableCell>
                                    <TableCell>{event.businessId}</TableCell>
                                </TableRow>
                            ))}

                            {!eventosFiltrados.length && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        No hay eventos con ese businessId.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Grid>
            </Grid>
        </Paper>
    );
}